import { TenantDto } from '@app/stores/tenants/models/tenant.model';

export type PermissionMatrixKey =
  'member' | 'create' | 'read' | 'update' | 'delete' | 'analytics' | 'permission';

export const PERMISSION_MATRIX_KEYS: PermissionMatrixKey[] = [
  'member',
  'create',
  'read',
  'update',
  'delete',
  'analytics',
  'permission',
];

export const PERMISSION_MATRIX_LABELS: Record<PermissionMatrixKey, string> = {
  member: 'M',
  create: 'C',
  read: 'R',
  update: 'U',
  delete: 'D',
  analytics: 'A',
  permission: 'P',
};

export type ItemType =
  'DIVISION' | 'BRAND' | 'WORKSPACE' | 'CAMPAIGN' | 'PROJECT' | 'STANDALONE_PROJECT';

export const ITEM_TYPES: ItemType[] = [
  'DIVISION',
  'BRAND',
  'WORKSPACE',
  'CAMPAIGN',
  'PROJECT',
  'STANDALONE_PROJECT',
];

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  DIVISION: 'Division',
  BRAND: 'Brands',
  WORKSPACE: 'Workspaces',
  CAMPAIGN: 'Campaigns',
  PROJECT: 'Projects',
  STANDALONE_PROJECT: 'Standalone Projects',
};

export interface PermissionMatrix {
  member: boolean;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  analytics: boolean;
  permission: boolean;
}

export function emptyMatrix(): PermissionMatrix {
  return {
    member: false,
    create: false,
    read: false,
    update: false,
    delete: false,
    analytics: false,
    permission: false,
  };
}

// ─── Groups ─────────────────────────────────────────────────────────────────

export interface GroupDto {
  accountUuid: string;
  accountId?: number;
  groupName: string;
  email?: string;
  tenantId?: number;
  tenantUuid?: string;
  groupType?: string;
}

export interface CreateGroupPayload {
  groupName: string;
  email: string;
  groupType: string;
  tenantUuid: string;
}

export interface UpdateGroupPayload {
  groupName: string;
}

// ─── Accounts / Users ────────────────────────────────────────────────────────

export type AccountStatus = 'active' | 'inactive' | 'blocked';
export type LicenceType = 'VIEWER' | 'STANDARD' | 'PREMIUM' | 'ULTIMATE';
export type AccountCategory = 'USER' | 'GROUP' | 'BOT' | 'SYSTEM';

export interface AccountDto {
  accountUuid: string;
  accountId?: number;
  accountName?: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatarUrl?: string;
  nickName?: string;
  loginAccount?: boolean;
  createdAt: string;
  updatedAt: string;
  accountSecurity?: AccountSecurityDto;
  accountPreferences?: AccountPreferencesDto;
  tenantAccounts?: TenantAccountDto[];
}

export interface AccountSecurityDto {
  lastAuthAttempt: string;
  lastAuthSuccess: string;
  blocked: boolean;
  blockedAt: string;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AccountPreferencesDto {
  twoFactorEnabled?: boolean;
  mfaMechanism?: string;
  extra: {
    additionalProp1: string;
    additionalProp2: string;
    additionalProp3: string;
  };
}

export interface TenantAccountDto {
  tenantId: number;
  tenantAdmin?: boolean;
  blocked: boolean;
  invited: boolean;
  currentTenant: boolean;
  createdAt: string;
  tenant: TenantDto;
  roles: AccountRoleDto[];
  groups: AccountGroupDto[];
}

export interface AccountRoleDto {
  accountId: number;
  accountUuid: string;
  accountName: string;
  accountCategory?: string;
}

export interface AccountGroupDto {
  accountId: number;
  accountUuid: string;
  accountName: string;
  accountCategory?: string;
}

export interface TenantAssignment {
  tenantUuid: string;
  tenantAdmin: boolean;
  blocked: boolean;
  // Role names to assign / unassign in this tenant, diffed against the account's current roles.
  addRoles?: string[];
  removeRoles?: string[];
}

export interface RoleDto {
  accountUuid: string;
  roleName: string;
  tenantUuid: string;
}

export interface AccountPostPayload {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  nickName?: string;
  accountName?: string;
  password?: string;
}

// ─── Features ────────────────────────────────────────────────────────────────

export interface FeatureDto {
  featureId: number;
  name: string;
  description: string;
  featureDetailCount: number;
  createdAt: string;
}

export interface FeatureDetailDto {
  featureDetailId: number;
  featureId: number;
  name: string;
  description: string;
  defaultMatrix: Record<string, unknown>;
  deletedAt?: string | null;
  createdAt: string;
}

// ─── Permissions ─────────────────────────────────────────────────────────────

export interface PermissionDto {
  accountUuid: string;
  featureDetailName: string;
  itemUuid: string;
  itemType?: ItemType;
  isOwner: boolean;
  matrix: PermissionMatrix;
  updatedAt: string;
}

export interface GrantPermissionPayload {
  accountUuid: string;
  featureDetailName: string;
  itemUuid: string;
  matrix: PermissionMatrix;
  isOwner?: boolean;
}

export interface UpdatePermissionPayload {
  matrix?: PermissionMatrix;
  isOwner?: boolean;
}

// ─── Invitations ─────────────────────────────────────────────────────────────

export type InvitationStatus = 'active' | 'expired' | 'accepted' | 'revoked';

export interface InvitationDto {
  invitationId: number;
  tenantUuid: string;
  email: string;
  code: string;
  status: InvitationStatus;
  expiredAt: string;
  createdBy: string;
  createdAt: string;
}

export interface SendInvitationPayload {
  email: string;
  accountCategoryName?: string;
}
